import {
  Card, Container, StatusMessage, TextualLink, Tooltip, Message,
} from '@hvcgroep/design-system';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Termijnbedrag from '@hvcgroep/design-system/assets/illustrations/Termijnbedrag.svg';
import Adres from '@hvcgroep/design-system/assets/illustrations/Adres.svg';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ContractSwitcherHeader from '../../containers/ContractSwitcherHeader';
import useContract from '../../hooks/useContract';
import formatCurrency from '../../helpers/formatCurrency';
import InvoiceTable from '../../components/InvoiceTable';
import formatAddress from '../../helpers/formatAddress';
import usePaginatedInvoices from '../../hooks/usePaginatedInvoices';
import SkeletonLoader from '../../components/SkeletonLoader';
import ErrorPage from '../ErrorPage';

const Overview: React.FC = () => {
  const { t } = useTranslation();
  const heading = t('pages.overview.title');
  const siteTitle = t('general.siteTitle');

  const {
    selectedAccount,
    selectedContract,
    canAdjustAdvancePayment,
    isLoading: isContractsLoading,
    isError,
  } = useContract();

  const { invoices, isLoading: isInvoicesLoading } = usePaginatedInvoices(selectedAccount?.id);

  const isLoading = isContractsLoading || isInvoicesLoading;

  const mostRecentInvoices = invoices.slice(0, 3);

  const hasTaskId = !!selectedContract?.taskId;
  const hasBudgetBillCounter = !!selectedAccount?.budgetBillCounter;
  const budgetBillCounter = Number(selectedAccount?.budgetBillCounter);

  let adjustmentsText;
  if (budgetBillCounter === 1) {
    adjustmentsText = t('pages.overview.adjustmentsOne');
  } else {
    adjustmentsText = t('pages.overview.adjustmentsTwoOrZero');
  }

  const finalExplanation = `${String(t('pages.overview.advancePaymentExplanation'))} ${String(adjustmentsText)}`;

  const isContractDatePassed = (() => {
    if (selectedContract?.endDate) {
      const endDate = new Date(selectedContract.endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set time to midnight for a fair comparison

      return endDate <= today;
    }
    // If there is no endDate, the contract cannot have expired
    return false;
  })();

  if (isError) {
    return <ErrorPage type="serverError" />;
  }

  return (
    <Container fluid>
      {hasTaskId && (
        <Message
          type="warning"
          actionButton={(
            <TextualLink>
              <Link to="/meterstanden-doorgeven">{t('pages.overview.submitMeterReadings')}</Link>
            </TextualLink>
          )}
        >
          {t('pages.overview.message')}
        </Message>
      )}

      <Helmet><title>{`${heading} - ${siteTitle}`}</title></Helmet>
      {isLoading && (
        <SkeletonLoader type="overview" />
      )}
      { !isLoading && (
      <>
        <ContractSwitcherHeader heading={heading} />
        <div className="u-margin--bottom--6">
          {(selectedAccount?.advancePayment != null) && (
          <Card
            heading={t('advancePayment.yourAdvancePayment')}
            image={Termijnbedrag}
            {...(!isContractDatePassed && {
              actionButton: canAdjustAdvancePayment ? (
                <TextualLink asChild>
                  <Link to="/termijnbedrag-wijzigen">{t('advancePayment.adjustAdvancePayment')}</Link>
                </TextualLink>
              ) : (
                <>
                  <div className="d-flex">
                    <p className="text-bold text-muted">{t('advancePayment.adjustAdvancePayment')}</p>
                    <Tooltip title={t('pages.overview.toolTipMessage')} tooltipId="tooltip_desc" />
                  </div>
                </>
              ),
            })}
          >
            <>
              { !isContractDatePassed && (
                <>
                  <span className="h2">
                    {formatCurrency(selectedAccount.advancePayment)}
                  </span>

                  <p className="u-margin--top--4">
                    { !hasBudgetBillCounter
                      ? t('pages.overview.advancePaymentExplanation')
                      : finalExplanation }
                  </p>
                </>
              )}
              { isContractDatePassed && (
                <p className="u-margin--top--4">
                  {t('pages.overview.contractDatePassed')}
                </p>
              )}
            </>
          </Card>
          )}
        </div>
        <div className="u-margin--bottom--6">
          <h2 className="h3">{t('pages.invoices.title')}</h2>

          <InvoiceTable
            invoices={mostRecentInvoices}
            className="u-margin--bottom--4"
          />
          {invoices.length === 0 ? (
            <StatusMessage type="info">
              {t('invoiceTable.noInvoicesFound')}
            </StatusMessage>
          ) : (
            <TextualLink asChild>
              <Link to="/facturen">
                {t('pages.overview.viewAllInvoices')}
              </Link>
            </TextualLink>
          )}

        </div>

        {selectedContract && (
        <Card
          heading={t('pages.overview.deliveryAddress')}
          image={Adres}
          actionButton={
            (
              <TextualLink asChild>
                <Link to="/mijn-gegevens">
                  {t('pages.overview.viewContractDetails')}
                </Link>
              </TextualLink>
            )
          }
        >
          {selectedContract.address && formatAddress(selectedContract.address)}
        </Card>
        )}
      </>
      ) }
    </Container>
  );
};

export default Overview;
